/**
 * G14. Пенсия: «Что оставить». При решении `work.retire` — разложить
 * шесть «наследств» (деньги, дом, письмо, вещь, ничего, долг) по
 * получателям: дети (если есть) и когорта. Раскладка хранится строкой в
 * `flags.legacy` («money:children,house:cohort,…»); её прочтут отношения
 * NPC после смерти и текст свидетельства (планы 3+; движок пока не
 * читает). Разложил — «на покой» (`retire`); `input.stay` — работать
 * дальше. «Пропустить» — автовыбор решения; если это пенсия, всё
 * остаётся первому получателю.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome } from '@life/engine';
import type { MinigameDef } from '../types';
import { optionAvailable, pendingOf } from '../skill/common';
import { nothing } from './common';

export const FLAG_LEGACY = 'legacy';
export const LEGACY_ITEMS = ['money', 'house', 'letter', 'thing', 'nothing', 'debt'] as const;
export type LegacyItemId = (typeof LEGACY_ITEMS)[number];
export const LEGACY_RECIPIENTS = ['children', 'cohort'] as const;
export type LegacyRecipient = (typeof LEGACY_RECIPIENTS)[number];

export interface LegacyItem {
  id: LegacyItemId;
  key: string;
}

export interface LegacyState {
  items: LegacyItem[];
  /** Доступные получатели: дети только при детях. */
  recipients: LegacyRecipient[];
  timeoutOption: string;
}

/** `to[i]` — индекс получателя для `items[i]`; нет/вне диапазона — первый получатель. */
export type LegacyInput = { to: number[]; stay?: boolean };

const LEGACY_KINDS = ['work.retire'] as const;

export function legacyItemKey(id: string): string {
  return `minigame.legacy.item.${id}`;
}

export function legacyRecipientKey(id: string): string {
  return `minigame.legacy.to.${id}`;
}

/** Раскладка → строка флага. */
export function encodeLegacy(layout: Readonly<Record<LegacyItemId, LegacyRecipient>>): string {
  return LEGACY_ITEMS.map((id) => `${id}:${layout[id]}`).join(',');
}

/** Строка флага → раскладка; неизвестные пары пропускаются. */
export function parseLegacy(raw: string): Partial<Record<LegacyItemId, LegacyRecipient>> {
  const out: Partial<Record<LegacyItemId, LegacyRecipient>> = {};
  for (const pair of String(raw).split(',')) {
    const [id, to] = pair.split(':');
    if ((LEGACY_ITEMS as readonly string[]).includes(id ?? '') && (LEGACY_RECIPIENTS as readonly string[]).includes(to ?? '')) {
      out[id as LegacyItemId] = to as LegacyRecipient;
    }
  }
  return out;
}

function layoutOf(state: LegacyState, to: readonly number[]): Record<LegacyItemId, LegacyRecipient> {
  const first = state.recipients[0] ?? 'cohort';
  const layout = {} as Record<LegacyItemId, LegacyRecipient>;
  state.items.forEach((item, i) => {
    const r = to[i];
    layout[item.id] = Number.isInteger(r) && r !== undefined && r >= 0 && r < state.recipients.length ? state.recipients[r]! : first;
  });
  return layout;
}

export const legacy: MinigameDef<LegacyInput, LegacyState> = {
  id: 'legacy',
  section: 'G',
  triggers: [{ kind: 'decision', decision: 'work.retire' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return pendingOf(life, LEGACY_KINDS) !== null;
  },
  setup(life: Life): LegacyState {
    const recipients: LegacyRecipient[] = life.children > 0 ? ['children', 'cohort'] : ['cohort'];
    return {
      items: LEGACY_ITEMS.map((id) => ({ id, key: legacyItemKey(id) })),
      recipients,
      timeoutOption: pendingOf(life, LEGACY_KINDS)?.timeoutOption ?? 'continue',
    };
  },
  play(state: LegacyState, input: LegacyInput, life: Life): MinigameOutcome {
    const decision = pendingOf(life, LEGACY_KINDS);
    const canRetire = decision ? optionAvailable(decision, 'retire') : true;
    if (input.stay === true || !canRetire) return { ...nothing(), decisionOption: 'continue' };
    return {
      ...nothing(),
      decisionOption: 'retire',
      flags: { [FLAG_LEGACY]: encodeLegacy(layoutOf(state, Array.isArray(input.to) ? input.to : [])) },
      logKey: 'minigame.legacy.done',
    };
  },
  expected(state: LegacyState): MinigameOutcome {
    const outcome: MinigameOutcome = { ...nothing(), decisionOption: state.timeoutOption };
    if (state.timeoutOption === 'retire') outcome.flags = { [FLAG_LEGACY]: encodeLegacy(layoutOf(state, [])) };
    return outcome;
  },
};
